import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

/** Catches render errors so a bug shows a recoverable screen instead of a blank window. */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`DTS Mail crashed: ${error.message}
${error.stack ?? ''}
Component stack:${info.componentStack ?? ''}`);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="relative z-10 flex h-full items-center justify-center p-8">
        <div className="drag absolute inset-x-0 top-0 h-12" />
        <div className="glass w-full max-w-lg rounded-3xl bg-raised/80 p-8 shadow-2xl">
          <div className="text-[20px] font-semibold">Something went wrong</div>
          <p className="mt-1 text-[13px] text-muted">DTS Mail hit an unexpected error. Your mail and drafts are safe on the server.</p>
          <pre className="selectable mt-4 max-h-48 overflow-auto rounded-xl bg-bg/60 p-3 font-mono text-[11.5px] text-danger">
            {this.state.error.message}
          </pre>
          <div className="mt-5 flex gap-2">
            <button
              className="h-10 rounded-xl bg-accent px-4 text-[13.5px] font-semibold text-accent-fg hover:brightness-110"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
            <button className="h-10 rounded-xl bg-line px-4 text-[13.5px] font-medium hover:bg-line-strong" onClick={() => this.setState({ error: null })}>
              Try to continue
            </button>
          </div>
        </div>
      </div>
    );
  }
}
